/**
 * Authentication API
 * Handles login/logout with sidecar
 */

import { saveToken, clearToken, clearAllTokens, getToken, saveFetchCredentials } from '../utils/storage.js';
import { getMachineBaseURL } from '../utils/config.js';

function getAuthBase(machine) {
    const base = getMachineBaseURL(machine);
    return base ? `${base}/api/auth` : './api/auth';
}

function getMachineName(machine) {
    return machine?.name || 'default';
}

/**
 * Check if current JWT token is valid
 * @returns {Promise<{valid: boolean, exp?: number}>} Auth status
 */
export async function checkAuthStatus(machine) {
    const machineName = getMachineName(machine);
    const token = getToken(machineName);

    if (!token) {
        return { valid: false };
    }

    const url = `${getAuthBase(machine)}/status`;
    
    try {
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            clearToken(machineName);
            return { valid: false };
        }
        
        const data = await response.json();
        return data;
        
    } catch (error) {
        console.error('Auth status check failed:', error);
        return { valid: false };
    }
}

/**
 * Login to sidecar with secret
 * @param {string} secret - API secret
 * @returns {Promise<object>} Response data
 */
export async function login(secret, machine) {
    if (!secret) {
        throw new Error('Secret is required');
    }

    const machineName = getMachineName(machine);
    const url = `${getAuthBase(machine)}/login`;
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ secret }),
        });
        
        const data = await response.json();
        
        if (data.error) {
            throw new Error(data.message || 'Login failed');
        }
        
        saveToken(data.token, machineName);
        saveFetchCredentials(data.fetchToken, machineName);
        return data;
        
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
}

/**
 * Logout - call server to clear HttpOnly cookie, then clear client JWT
 */
export async function logout(machine) {
    const machineName = getMachineName(machine);

    // Call server logout to clear the HttpOnly auth cookie
    try {
        const url = `${getAuthBase(machine)}/logout`;
        await fetch(url, {
            method: 'POST',
            credentials: 'include',
        });
    } catch (_) {
        // Silently ignore — always proceed to clear client-side JWT
    }

    if (machine) {
        clearToken(machineName);
        return;
    }
    clearAllTokens();
}
